import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { P, type Tenant, type TenantSettings as TS } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { fieldErrors } from '../../../lib/errors';
import { useMut } from '../../../lib/query';
import { Alert, Button, Card, Columns, ErrorState, Field, Loading, PageHeader, Row, Screen, Select, Stack, Switch, Text, TextField } from '../../../ui';
import { AutoGrid, Chip, clone, NumField } from '../../payroll/components/common';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TZ = ['Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Kathmandu', 'Asia/Dhaka', 'Europe/London', 'America/New_York', 'UTC'];
const MONTH_NAMES = Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleString('en-IN', { month: 'long' }));

export function TenantSettingsScreen() {
  const { can } = useAuth();
  const edit = can(P.TENANT_MANAGE);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['tenant'], queryFn: () => api.admin.tenant() });
  const [t, setT] = useState<Tenant | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  useEffect(() => {
    if (q.data) setT(clone(q.data));
  }, [q.data]);
  const save = useMut(() => api.admin.updateTenant({ name: t!.name, settings: t!.settings }), {
    invalidate: [['tenant']],
    success: 'Settings saved',
    onSuccess: () => {
      setErrs({});
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (e) => setErrs(fieldErrors(e)),
  });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data)
    return (
      <Screen>
        <PageHeader title="Tenant settings" />
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      </Screen>
    );
  if (!t) return <Loading />;
  const s = t.settings ?? {};
  const setS = (patch: Partial<TS>) => setT({ ...t, settings: { ...s, ...patch } });
  const att = s.attendance ?? {};
  const pay = s.payroll ?? {};
  const wo = s.week_offs ?? [0];
  const dirty = JSON.stringify(t) !== JSON.stringify(q.data);
  const e = (k: string) => errs[`settings.${k}`] ?? errs[k];

  return (
    <Screen testID="tenant-settings">
      <PageHeader
        title="Tenant settings"
        subtitle="Company-wide defaults for attendance, leave and payroll."
        actions={
          edit ? (
            <>
              <Button title="Discard" disabled={!dirty} onPress={() => setT(clone(q.data!))} />
              <Button variant="primary" title="Save" disabled={!dirty} loading={save.isPending} onPress={() => save.mutate(undefined)} testID="save-tenant" />
            </>
          ) : undefined
        }
      />
      {!edit && <Alert tone="info">You have view-only access to tenant settings.</Alert>}
      <Columns ratio={[1, 1]}>
        <Stack>
          <Card title="Organisation">
            <Stack gap={14}>
              <TextField label="Company name" required value={t.name} disabled={!edit} error={errs.name} onChangeText={(v) => setT({ ...t, name: v })} testID="tenant-name" />
              <AutoGrid min={180}>
                <Field label="Company code" hint="Used at sign in">
                  <Text variant="mono">{t.code}</Text>
                </Field>
                <Field label="Plan">
                  <Text>{t.plan}</Text>
                </Field>
              </AutoGrid>
              <AutoGrid min={180}>
                <Select label="Timezone" value={s.timezone ?? 'Asia/Kolkata'} disabled={!edit} error={e('timezone')} options={TZ.map((z) => ({ value: z, label: z }))} onChange={(v) => setS({ timezone: v })} />
                <Select label="Currency" value={s.currency ?? 'INR'} disabled={!edit} options={['INR', 'USD', 'AED', 'SGD', 'GBP'].map((c) => ({ value: c, label: c }))} onChange={(v) => setS({ currency: v })} />
              </AutoGrid>
            </Stack>
          </Card>
          <Card title="Weekly offs" subtitle="Default non-working days (shifts and rosters can override).">
            <Row gap={6} testID="week-offs">
              {DOW.map((d, i) => {
                const on = wo.includes(i);
                return <Chip key={d} label={d} selected={on} disabled={!edit} onPress={() => setS({ week_offs: on ? wo.filter((x) => x !== i) : [...wo, i].sort((a, b) => a - b) })} />;
              })}
            </Row>
            {!!e('week_offs') && <Text variant="small">{e('week_offs')}</Text>}
          </Card>
          <Card title="Leave">
            <Select label="Leave year starts in" value={s.leave?.year_start_month ?? 1} disabled={!edit} options={MONTH_NAMES.map((m, i) => ({ value: i + 1, label: m }))} onChange={(v) => setS({ leave: { ...s.leave, year_start_month: v } })} />
          </Card>
        </Stack>
        <Stack>
          <Card title="Attendance">
            <Stack gap={14}>
              <Switch label="Geo-fence required" description="Punches must be within the location's geo-fence radius" value={!!att.geo_fence_required} disabled={!edit} onChange={(v) => setS({ attendance: { ...att, geo_fence_required: v } })} />
              <Switch label="Selfie required" description="Web and mobile punches must include a selfie" value={!!att.selfie_required} disabled={!edit} onChange={(v) => setS({ attendance: { ...att, selfie_required: v } })} />
              <NumField
                label="Late marks per half day"
                hint="This many late arrivals in a month count as a half-day LOP"
                value={att.late_marks_per_half_day ?? 3}
                disabled={!edit}
                error={e('attendance.late_marks_per_half_day')}
                onChange={(v) => setS({ attendance: { ...att, late_marks_per_half_day: v ?? 0 } })}
                width={200}
              />
            </Stack>
          </Card>
          <Card title="Payroll">
            <AutoGrid min={180}>
              <NumField label="Pay day of month" value={pay.pay_day ?? 1} disabled={!edit} error={e('payroll.pay_day')} onChange={(v) => setS({ payroll: { ...pay, pay_day: v ?? 1 } })} />
              <NumField
                label="Variance threshold %"
                hint="Flag payslips whose net pay moves more than this vs last month"
                value={pay.variance_threshold_pct ?? 10}
                disabled={!edit}
                error={e('payroll.variance_threshold_pct')}
                onChange={(v) => setS({ payroll: { ...pay, variance_threshold_pct: v ?? 0 } })}
                testID="variance-threshold"
              />
            </AutoGrid>
          </Card>
        </Stack>
      </Columns>
    </Screen>
  );
}
